import React, { Suspense } from 'react';

/**
 * Error Boundary component for handling errors in lazy loaded components
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('LazyLoader error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback ? (
        this.props.fallback(this.state.error)
      ) : (
        <div className="min-h-screen flex flex-col items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md p-6 max-w-md w-full">
            <h2 className="text-xl font-semibold mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-4">
              There was a problem loading this component. This might be caused by a network issue or browser extension.
            </p>
            <button 
              onClick={() => window.location.reload()}
              className="w-full py-2 px-4 bg-gray-900 text-white rounded hover:bg-gray-800"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Default loading component
 */
const DefaultLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
  </div>
);

/**
 * LazyLoader component for handling lazy loaded components with proper error handling
 * 
 * @param {Object} props - Component props
 * @param {React.LazyExoticComponent} props.component - The lazy loaded component
 * @param {React.ReactNode} props.fallback - Optional custom loading component
 * @param {Function} props.errorFallback - Optional custom error component function that receives the error
 * @param {Object} props.componentProps - Props to pass to the loaded component
 */
const LazyLoader = ({ 
  component: Component, 
  fallback = <DefaultLoader />,
  errorFallback,
  ...componentProps 
}) => {
  return (
    <ErrorBoundary fallback={errorFallback}>
      <Suspense fallback={fallback}>
        <Component {...componentProps} />
      </Suspense>
    </ErrorBoundary>
  );
};

export default LazyLoader; 